<template>
    <div class="campaign-light__content-header library-header">
        <section-nav />
        <div class="campaign-light__content-header-items">
            <div class="library-header__select-all">
                <Checkbox
                    :disabled="paginatedDeliverables.length === 0 || selectAllDisabled"
                    :indeterminate="isSelectionIndeterminate"
                    :model-value="allPageDeliverablesAreSelected"
                    data-testid="library-header__select-all-checkbox"
                    @click.prevent="toggleDeliverableSelection"
                >
                    Select all
                </Checkbox>

                <span class="library-header__selected-count" data-testid="library-header__selected-count">
                    {{ numberOfSelectedDeliverables }} Selected
                </span>
                <span
                    v-if="userCanDownloadOrPublish"
                    class="library-header__selected-count"
                    data-testid="library-header__select-via-file"
                >
                    <Button icon="ios-cloud-upload-outline" size="small" @click="selectViaFile" />
                </span>
            </div>
            <div class="library-header__buttons">
                <div v-if="isTableView" class="campaign-light__content-header-item">
                    <CustomiseTableColumnsDropdown />
                </div>
                <div class="campaign-light__content-header-item">
                    <RadioGroup :model-value="viewMode" type="button" @onChange="setViewMode">
                        <Radio :label="ViewMode.Grid" data-testid="library-view-mode__grid">
                            <Icon type="md-apps" />
                        </Radio>
                        <Radio :label="ViewMode.Table" data-testid="library-view-mode__table">
                            <Icon type="md-list" />
                        </Radio>
                    </RadioGroup>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { mapStores } from "pinia";

import { USER_ACTIONS } from "hoxton-shared/enums/user";

import CustomiseTableColumnsDropdown from "@/components/Campaign/LibraryDeliverableTable/LibraryDeliverableTableCustomiseColumnsDropdown.vue";
import SectionNav from "@/components/SectionNav.vue";
import { ViewMode } from "@/enums/library";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";
import type { ConstructedDeliverable } from "@/types/types";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";
import { userCan } from "@/services/Auth";

export default defineComponent({
    name: "LibraryHeader",
    components: {
        CustomiseTableColumnsDropdown,
        SectionNav
    },

    props: {
        paginatedDeliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },
        selectAllDisabled: {
            type: Boolean
        },
        selectedDeliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },
        viewMode: {
            required: true,
            type: String
        },
        totalItems: {
            required: true,
            type: Number
        }
    },

    emits: ["viewModeSet", "selectedDeliverablesUpdated", "selectViaFile"],

    setup() {
        const ga4 = useGA4Tracking();

        return { ga4 };
    },

    data() {
        return {
            ViewMode
        };
    },

    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),
        allPageDeliverablesAreSelected() {
            return (
                this.hasDeliverablesSelected && this.paginatedDeliverables.length === this.selectedDeliverables.length
            );
        },

        userCanDownloadOrPublish() {
            return (
                userCan(USER_ACTIONS.canDownloadDeliverable, {
                    clientId: this.clientId,
                    campaignId: this.campaignId
                }) ||
                userCan(USER_ACTIONS.canPublishDeliverableToAdServer, {
                    clientId: this.clientId,
                    campaignId: this.campaignId
                })
            );
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        hasDeliverablesSelected() {
            return this.selectedDeliverables.length > 0;
        },

        isSelectionIndeterminate() {
            return (
                this.hasDeliverablesSelected &&
                !this.creativeLibraryFiltersStore.allSelectedMode &&
                !this.allPageDeliverablesAreSelected
            );
        },

        isTableView() {
            return this.viewMode === ViewMode.Table;
        },

        numberOfSelectedDeliverables() {
            if (this.creativeLibraryFiltersStore.allSelectedMode) {
                return this.totalItems;
            }

            return this.selectedDeliverables.length;
        }
    },

    created() {
        this.ViewMode = ViewMode;
    },

    methods: {
        setViewMode(viewMode: (typeof ViewMode)[keyof typeof ViewMode]) {
            this.$emit("viewModeSet", viewMode);
        },

        toggleDeliverableSelection() {
            if (!this.selectAllDisabled) {
                const isSelecting = !this.hasDeliverablesSelected || this.isSelectionIndeterminate;

                this.ga4.trackClickEvent({
                    interactionObject: GA4.CLICK_INTERACTION_OBJECTS.CHECKBOX,
                    interactionText: isSelecting
                        ? GA4.CLICK_INTERACTION_TEXTS.SELECT_ALL
                        : GA4.CLICK_INTERACTION_TEXTS.DESELECT_ALL
                });

                if (this.creativeLibraryFiltersStore.allSelectedMode) {
                    this.$emit("selectedDeliverablesUpdated", []);
                } else if (!this.hasDeliverablesSelected || this.isSelectionIndeterminate) {
                    this.$emit(
                        "selectedDeliverablesUpdated",
                        this.paginatedDeliverables.map(({ idHash }) => idHash)
                    );
                } else {
                    this.$emit("selectedDeliverablesUpdated", []);
                }
            }
        },

        selectViaFile() {
            this.$emit("selectViaFile");
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.library-header__buttons {
    align-items: center;
    display: flex;
    height: 100%;
    gap: 5px;
}

.library-header__group {
    align-items: center;
    display: flex;
    border-right: 1px solid $grey3;
    height: 100%;
    margin-right: 16px;
    padding-right: 20px;
}

.library-header__select-all {
    white-space: nowrap;
    margin-right: $spacing;
}

.library-header__selected-count {
    border-left: 1px solid $grey3;
    font-size: $font-size-base;
    padding: 0 0 0 $spacing-small;
}

.campaign-light__content-header-group--buttons {
    button {
        padding: 15px 20px;
    }
}
</style>
